import { useCallback, useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { SectionHead } from "./SectionHead";
import { dateTime } from "../lib/ct";

// The Command Center — R1 triage screen (R1-CC-01..07) in the admin design's LAYOUT (founder brief
// 30 Sep §3). Read-only across all nine agents: what needs a human, what is stuck, and what changed
// since you last looked. Owns no data; every row and every tile links into the section that does.
//
// The headline and the eyebrow are built from the per-device marker, which is production's own store
// for "since you last looked" — the design's "Six agents ran the platform while you slept" is
// illustrative and is not printed. The amber band appears ONLY when decisions are queued, and names
// the agents that actually have them. The design's "for sale", "owner attached" and "broker attached"
// tiles are NOT built: there is no market in R1 and no broker seat until R3.
// Plain language for the audit actions this panel can show. Signing in and token refreshes are
// dropped server-side (they are not changes), so nothing here describes arriving. An action with no
// entry falls back to its own words with the dots and underscores taken out, so a new action reads
// as English on the day it ships rather than as a key.
const SAID = {
  "credit.grant.welcome": "Welcome credits granted",
  "scope.grant": "A console queue was granted",
  "scope.revoke": "A console queue was taken back",
  "member.password_changed": "A member changed their password",
  "invite.issue": "An invitation went out",
  "invite.accept": "An invitation was accepted",
  "invite.revoke": "An invitation was revoked",
  "invite.resend_request": "Someone asked for their invitation again",
  "seat.add": "A seat was added",
  "document.moved": "A document was filed to a building",
  "document.re_run": "A document was read again",
  "document.withdrawn": "A document was withdrawn",
  "document.duplicate_hold_resolved": "A duplicate hold was resolved",
  "librarian.merge_applied": "Two records were merged",
  "autonomy.dial_set": "An autonomy dial was moved",
  "spend.pause_cleared": "The spend pause was cleared",
  "policy_gate.decide": "A policy gate made a call",
  "staff.password_rotated": "A staff password was rotated",
  "staff.totp_enrolled": "A staff member set up their authenticator",
  "staff.recovery_code_used": "A break-glass recovery code was used",
  "staff_escalation_email_failed": "An escalation email failed to send",
};
const plainly = (action) => SAID[action]
  || String(action || "Something changed").replace(/[._]/g, " ").replace(/^./, (c) => c.toUpperCase());

const cursorKey = (acct) => `juno_cc_since_${acct || "staff"}`;

const TILE_LINK = {
  ingestion: "/console/librarian",
  marks: "/console/underwriter/marks",
  credits: "/console/steward",
  funnel: "/console/rainmaker",
};

function Panel({ title, subtitle, testid, items, empty }) {
  return (
    <section className="cc-panel" data-testid={testid}>
      <div className="cc-panel-head">
        <h2 className="cc-panel-title">{title}</h2>
        <span className="cc-count" data-testid={`${testid}-count`}>{items.length}</span>
      </div>
      <p className="cc-panel-sub">{subtitle}</p>
      {items.length === 0 ? (
        <div className="cc-empty" data-testid={`${testid}-empty`}>{empty}</div>
      ) : (
        <ul className="cc-list">
          {items.map((it, i) => (
            <li key={i} className={`cc-row cc-${it.tone || "amber"}`}>
              <NavLink to={it.link} className="cc-row-link" data-testid={`${testid}-row-${i}`}>
                <span className="cc-agent">{it.agent}</span>
                <span className="cc-subject">{it.subject}</span>
                <span className="cc-why">{it.why}</span>
                <span className="cc-waited">{it.waiting ? `waiting ${it.waiting}` : ""}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Tile({ label, value, note, to, tone, testid }) {
  const body = (
    <>
      <span className={`cc-tile-value ${tone ? `cc-tile-${tone}` : ""}`}>{value}</span>
      <span className="cc-tile-label">{label}</span>
      <span className="cc-tile-note">{note}</span>
    </>
  );
  return to ? (
    <NavLink className="cc-tile" to={to} data-testid={testid}>{body}</NavLink>
  ) : (
    <div className="cc-tile cc-tile-flat" data-testid={testid}>{body}</div>
  );
}

// "N decisions are queued for you across the Librarian and the Steward." — the agent list is the
// agents that actually have one, in the order they appear, never a fixed four.
function agentsSentence(names) {
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

function hoursSince(iso) {
  const then = Date.parse(iso || "");
  if (!then) return null;
  const h = Math.floor((Date.now() - then) / 3600000);
  if (h < 1) return "under an hour";
  return `${h} hour${h === 1 ? "" : "s"}`;
}

export default function TheCommandCenter() {
  const { session, authedFetch } = useAuth();
  const acct = session?.account_id;
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [away, setAway] = useState(null);   // how long the marker says they were gone

  const load = useCallback(() => {
    const since = localStorage.getItem(cursorKey(acct));
    setAway(hoursSince(since));
    const qs = since ? `?since=${encodeURIComponent(since)}` : "";
    authedFetch(`/command-center/section${qs}`)
      .then((res) => {
        setD(res);
        if (res?.since_last?.now) localStorage.setItem(cursorKey(acct), res.since_last.now);
      })
      .catch((e) => setErr(String(e)));
  }, [authedFetch, acct]);
  useEffect(load, [load]);

  if (err) return <div className="gk-err" data-testid="command-center-error">{err}</div>;
  if (!d) return <div className="loading" data-testid="command-center-loading">Loading…</div>;

  const s = d.secondary;
  const sl = d.since_last;

  // Headline: from the marker, and only from the marker. Nothing invented, nothing rounded up.
  let headline;
  if (sl.first_look) {
    headline = "First look on this device — the marker is set now.";
  } else if (sl.count === 0) {
    headline = "Nothing changed since you last looked.";
  } else {
    const n = `${sl.count}${sl.truncated ? "+" : ""}`;
    headline = `${n} ${sl.count === 1 && !sl.truncated ? "thing" : "things"} changed since you last looked.`;
  }
  const eyebrow = sl.first_look ? "THE COMMAND CENTER · FIRST LOOK"
    : away ? `UNATTENDED · ${away.toUpperCase()}` : "THE COMMAND CENTER · SINCE YOU LAST LOOKED";

  // The amber band: real decisions, real agents, or nothing at all.
  const queued = d.needs_human.items;
  const agents = [];
  const agentLink = {};
  queued.forEach((it) => {
    if (!agentLink[it.agent]) { agentLink[it.agent] = it.link; agents.push(it.agent); }
  });

  return (
    <div data-testid="the-command-center">
      <SectionHead eyebrow={eyebrow} headline={headline} testid="cc" />

      {queued.length > 0 && (
        <div className="cc-band" data-testid="cc-decision-band">
          <span className="cc-band-dot" aria-hidden="true" />
          <div className="cc-band-text">
            <p>
              <strong data-testid="cc-band-count">{queued.length} decision{queued.length === 1 ? "" : "s"}</strong>
              {" "}
              {queued.length === 1 ? "is" : "are"} queued for you across {agentsSentence(agents)}.
            </p>
            <p className="cc-band-links">
              {agents.map((a) => (
                <NavLink key={a} to={agentLink[a]} data-testid={`cc-band-link-${a.replace(/ /g, "-").toLowerCase()}`}>
                  {a}
                </NavLink>
              ))}
            </p>
            <span className="cc-band-note">Nothing is blocked — the agents kept working around them.</span>
          </div>
        </div>
      )}

      <div className="cc-tile-block">
        <span className="cc-block-eyebrow">The platform right now</span>
        <div className="cc-tiles" data-testid="cc-tiles">
          <Tile testid="cc-metric-ingestion" label="Documents in the pipeline"
                value={s.ingestion.present ? s.ingestion.total : "—"}
                note={s.ingestion.present
                  ? Object.entries(s.ingestion.counts).map(([k, v]) => `${v} ${k}`).join(" · ")
                  : s.ingestion.absent_reason}
                to={s.ingestion.present ? TILE_LINK.ingestion : null} />
          <Tile testid="cc-metric-marks" label="Marks awaiting approval"
                value={s.marks.present ? s.marks.awaiting_approval : "—"}
                tone={s.marks.present && s.marks.awaiting_approval > 0 ? "amber" : ""}
                note={s.marks.present ? `${s.marks.on_shelf} on the shelf` : s.marks.absent_reason}
                to={s.marks.present ? TILE_LINK.marks : null} />
          <Tile testid="cc-metric-credits" label="Credits consumed"
                value={s.credits.present ? s.credits.consumed : "—"}
                note={s.credits.present
                  ? `${s.credits.granted} granted · ${s.credits.cash_note}`
                  : s.credits.absent_reason}
                to={s.credits.present ? TILE_LINK.credits : null} />
          <Tile testid="cc-metric-funnel" label="Accounts"
                value={s.funnel.present ? s.funnel.accounts : "—"}
                note={s.funnel.present
                  ? Object.entries(s.funnel.by_state).map(([k, v]) => `${v} ${k}`).join(" · ")
                  : s.funnel.absent_reason}
                to={s.funnel.present ? TILE_LINK.funnel : null} />
        </div>
      </div>

      <Panel title="What needs a human right now" testid="cc-needs-human"
             subtitle="Across all nine agents — oldest waiting first. Each row opens the section that owns it."
             items={d.needs_human.items}
             empty="Nothing needs a human right now. This is real, not a placeholder — when a mark, a held document, a conflict or an escalation is waiting, it appears here first." />

      <Panel title="Stuck, failed or past a time limit" testid="cc-stuck"
             subtitle="Breached deadlines, failed tasks, expired locks and the spend pause — the things that won't clear themselves."
             items={d.stuck.items}
             empty="Nothing is stuck or past a limit. No breached escalations, expired locks or failed tasks, and no spend pause in effect." />

      <section className="cc-panel" data-testid="cc-since-last">
        <div className="cc-panel-head">
          <h2 className="cc-panel-title">Since you last looked</h2>
          {!sl.first_look && <span className="cc-count">{sl.count}{sl.truncated ? "+" : ""}</span>}
        </div>
        <p className="cc-panel-sub">{sl.note}</p>
        <p className="cc-device-note" data-testid="cc-since-device-note">
          This marker is per device — if you last looked on another device, that visit isn't counted here.
        </p>
        {sl.first_look ? (
          <div className="cc-empty" data-testid="cc-since-last-firstlook">Your marker is set now — next visit shows what changed while you were away.</div>
        ) : sl.items.length === 0 ? (
          <div className="cc-empty" data-testid="cc-since-last-empty">
            Nothing changed since you last looked. Signing in and refreshing a session don't count as
            changes, so this stays empty until something actually happens.
          </div>
        ) : (
          <ul className="cc-changelog">
            {sl.items.map((it, i) => (
              <li key={i} className="cc-change-row" data-testid={`cc-change-${i}`}>
                <span className="cc-change-action">{plainly(it.action)}</span>
                <span className="cc-change-subject">{it.rationale || ""}</span>
                <span className="cc-change-at">{dateTime(it.at)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="cc-panel cc-autonomy" data-testid="cc-metric-autonomy">
        <div className="cc-panel-head">
          <h2 className="cc-panel-title">Autonomy at a glance</h2>
        </div>
        <p className="cc-panel-sub">{s.autonomy.note}</p>
        {s.autonomy.off_default.length === 0 ? (
          <div className="cc-autonomy-clean" data-testid="cc-autonomy-clean">
            Every dial is on the default — <span className="mono">{s.autonomy.default}</span>. Nothing has been moved off.
          </div>
        ) : (
          <ul className="cc-autonomy-list">
            {s.autonomy.off_default.map((a, i) => (
              <li key={i} className="cc-autonomy-row" data-testid={`cc-autonomy-off-${i}`}>
                <span className="mono">{a.agent} · {a.label}</span>
                <span className="cc-autonomy-dial">{a.effective}{a.ceilinged ? " (ceilinged)" : ""}</span>
              </li>
            ))}
          </ul>
        )}
        <NavLink to="/console/floor-manager" className="cc-autonomy-link" data-testid="cc-autonomy-link">
          Open the Floor Manager to change a dial →
        </NavLink>
      </section>

      <p className="cc-constraint" data-testid="command-center-constraint">{d.constraint}</p>
    </div>
  );
}
